/**
 * UNKNOWN IS NOT DENIED — the four-state answer to "may this reader do this",
 * for the screens that change who can do what.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS RATHER THAN `permissions.has(key)`
 * ---------------------------------------------------------------------------
 * `usePermissions()` resolves a failed or in-flight read to an EMPTY SET, for a
 * default-deny reason that is correct: a control must not appear because the
 * answer has not arrived. But `has(key) === false` then carries three unrelated
 * facts at once —
 *
 *   * the set has not arrived yet,
 *   * the set could not be read at all, and
 *   * the reader genuinely does not hold the key
 *
 * — and a screen that renders the third sentence off the first two tells a
 * person they lack access they may well hold. `RequirePermission.tsx` is the
 * reference implementation of the distinction ("Unknown is not denied. When the
 * permission set could not be read, the set is empty — and answering that with
 * the No-permission screen accuses the user of lacking access they may well
 * hold"). It applies the rule to a whole route. This applies the same rule to a
 * SECTION, a button, or a form, which is what an authority-administration
 * screen is made of: one page carrying `orgchart:read`, `orgchart:delegate`,
 * `role:compose`, `role:assign` and a superuser TIER that is not a permission
 * at all, each of which can be held or not held independently.
 *
 * The lane brief for these screens is blunt about the stakes: seven admin
 * screens already claim a refusal from a permission set that has not arrived,
 * and a lane whose subject IS permissions is the worst possible place to add an
 * eighth.
 *
 * ---------------------------------------------------------------------------
 * `allowed` CAN FIRE UNDER `isError`, ON PURPOSE
 * ---------------------------------------------------------------------------
 * A cached set from before a failed refresh is still a real answer, and a
 * reader who holds the key should keep their controls rather than have them
 * yanked away by a refresh that failed. `RequirePermission` takes the same
 * position (`isError && !has(permission)`), and the ordering below is what
 * encodes it: the held case is decided FIRST, so a stale-but-affirmative set
 * wins over the failure.
 */
import type { PermissionsState } from './permissions.js';

/**
 * What a permission-shaped control should be showing.
 *
 * Four members, and none of them is a boolean. `unresolved` and `unknown` are
 * deliberately separate even though both suppress the control: they call for
 * different words ("checking" vs. "we couldn't check"), and only one of them
 * has a retry that means anything.
 */
export type AuthorityGate =
  /** The permission set has not arrived. Not a refusal. Show a pending state. */
  | { readonly kind: 'unresolved' }
  /**
   * The permission set could not be read. Not a refusal either — say what
   * happened and offer the retry that fixes it, exactly as `RequirePermission`
   * does. NEVER render "you do not have access" from this.
   */
  | { readonly kind: 'unknown' }
  /** A RESOLVED set that does not contain the key. This is an answer. */
  | { readonly kind: 'refused'; readonly permission: string }
  /** Held, at some scope. */
  | { readonly kind: 'allowed' };

/**
 * The gate's answer without its payload — the four names on their own.
 *
 * ADDED BY THE UNKNOWN-IS-NOT-DENIED SWEEP, deliberately and in this file
 * rather than as a second spelling somewhere else. A pure helper that has to
 * branch on all four states (`visitLogic.clientNameResolver`) cannot take the
 * whole `AuthorityGate` without taking a `permission` string it has no use for,
 * and writing the union out again in the consumer is how a fifth state, or a
 * renamed one, gets to exist in one file and not the other. `AuthorityGate` is
 * still the type a SCREEN passes around; this is for the pure functions it
 * calls.
 */
export type AuthorityGateKind = AuthorityGate['kind'];

/** True only for `allowed` — the one state entitled to render an operable control. */
export function gateOpen(gate: AuthorityGate): boolean {
  return gate.kind === 'allowed';
}

/**
 * True only for `refused` — the one state entitled to say "you may not".
 *
 * Written as its own predicate because `!gateOpen(gate)` is the collapse this
 * module exists to prevent, and `!gateOpen` is the expression somebody reaches
 * for when they want the refusal copy.
 */
export function gateRefused(gate: AuthorityGate): boolean {
  return gate.kind === 'refused';
}

/**
 * Does this reader hold `permission`, and if not, WHY not.
 *
 * `has()` and not `can()`: this is question 1 in `permissions.ts`'s vocabulary
 * — may the control be revealed — and a program-scoped grant is a real grant
 * that the server may well honour, narrowed. The server decides; the client's
 * job is not to refuse a reader the door before the server has been asked.
 */
export function authorityGate(
  state: Pick<PermissionsState, 'has' | 'isLoading' | 'isError'>,
  permission: string,
): AuthorityGate {
  // FIRST, and before either failure branch: a set that says yes is an answer,
  // even if the most recent refresh of it failed.
  if (state.has(permission)) return { kind: 'allowed' };
  if (state.isLoading) return { kind: 'unresolved' };
  if (state.isError) return { kind: 'unknown' };
  return { kind: 'refused', permission };
}

/**
 * The gate for a door that charges MORE THAN ONE key.
 *
 * `POST /orgchart/roles/assign` is the case it was written for: the route
 * checks `role:compose` and then checks `role:assign` separately, and a reader
 * holding one of the two gets a 403 naming the other. A screen that gated on
 * either key alone would offer a control that always refuses.
 *
 * The refusal names the FIRST key the reader is missing rather than all of
 * them, because the sentence a person acts on is "ask for X" — and an
 * unresolved or unreadable set anywhere in the list makes the whole answer
 * unresolved or unreadable, never a refusal.
 */
export function authorityGateAll(
  state: Pick<PermissionsState, 'has' | 'isLoading' | 'isError'>,
  permissions: readonly string[],
): AuthorityGate {
  if (permissions.length === 0) {
    // A door charging no key is a programming error, not an open door. Refusing
    // an empty list would be a lie about a permission nobody named, so this is
    // the one case that resolves to `unknown`: we cannot say.
    return { kind: 'unknown' };
  }
  let missing: string | null = null;
  for (const permission of permissions) {
    const gate = authorityGate(state, permission);
    // An unresolved or unreadable set poisons the whole conjunction: we do not
    // know whether the reader holds the rest, so we must not claim they do not.
    if (gate.kind === 'unresolved' || gate.kind === 'unknown') return gate;
    if (gate.kind === 'refused' && missing === null) missing = gate.permission;
  }
  return missing === null ? { kind: 'allowed' } : { kind: 'refused', permission: missing };
}

/**
 * The sentence for a gate that is not open, or `null` when it is.
 *
 * Copy lives here rather than in each section so the three non-open states
 * cannot be worded into each other by a screen that only remembered two of
 * them. `what` completes "…to <what>" and should be a verb phrase the reader
 * recognises: "assign coverage", "compose a role", "designate a superuser".
 */
export function gateSentence(gate: AuthorityGate, what: string): string | null {
  switch (gate.kind) {
    case 'allowed':
      return null;
    case 'unresolved':
      return `Checking whether you may ${what}…`;
    case 'unknown':
      return `We couldn’t check whether you may ${what}. This is a connection problem, not a decision about your account — nothing here has been withheld from you.`;
    case 'refused':
      return `You may not ${what}. This needs the ${gate.permission} permission, which you do not currently hold. Trying again will repeat this answer; a grant is what changes it.`;
  }
}

/**
 * THE GATE A SERVER REFUSAL IMPLIES — the other way a refusal reaches a screen.
 *
 * ---------------------------------------------------------------------------
 * WHY THE PERMISSION SET IS NOT ENOUGH ON ITS OWN
 * ---------------------------------------------------------------------------
 * `authorityGate` above answers from the reader's own permission set, which is
 * the right instrument for a control the screen decides whether to render. It
 * cannot answer for a request the server has already refused, and the two
 * disagree more often than they look like they should:
 *
 *   * `usePermissions().has` reads the FLATTENED key list and is scope-blind,
 *     while every guard these panels sit behind is `can()` at ORGANIZATION
 *     scope. A program-scoped `rules:read` passes the client gate and is a 403
 *     at the door — `permissions.ts`'s `OrgWideReader` docblock is entirely
 *     about this gap.
 *   * A route can charge a SECOND key the screen never asked for.
 *     `GET /rules/:id/alerts` adds `directory:read` on top of `rules:read`
 *     because "a rule's alerts name the worker they are about".
 *
 * So a panel that renders off `query.isError` alone collapses two facts that
 * want opposite words: a 403 is a SETTLED ANSWER, and everything else is a
 * fault that a retry might genuinely fix. Offering "try again" beside a
 * settled answer promises a different outcome that cannot arrive — every press
 * returns the same 403 — and it never says which grant would change it.
 *
 * ---------------------------------------------------------------------------
 * `charged` IS A FALLBACK, NOT AN OVERRIDE
 * ---------------------------------------------------------------------------
 * The server names the key when it knows which one is missing
 * (`requireDirectoryRead` sends `permission: 'directory:read'`); the base
 * `guard` in the same file sends a bare "Permission denied." So the response's
 * own key wins when it is there, and `charged` — the key the CALLER knows this
 * route asks for — is what keeps the sentence from being keyless when it is
 * not. A refusal that cannot name a key is a refusal nobody can act on.
 *
 * 401 IS DELIBERATELY NOT A REFUSAL. It means the session is gone, which is
 * the shell's business and not this panel's; treating it as `refused` would
 * tell somebody they lack a permission when what they lack is a session.
 */
export function refusalGate(error: unknown, charged: string): AuthorityGate | null {
  const shape = error as { status?: unknown; body?: { permission?: unknown } } | null | undefined;
  if (shape?.status !== 403) return null;
  const named = shape.body?.permission;
  return { kind: 'refused', permission: typeof named === 'string' && named ? named : charged };
}
